import type { FallbackTier, IncidentFeature, WeatherSnapshot } from "@chonburi/shared";
import { PanelHeader } from './PanelHeader';
import { aqiBand, type AirBriefInput } from "../lib/mayorBrief";
import { feedState, reportsLevel, trafficLevel, LEVEL_WORD, type KpiLevel } from "../lib/kpi";

interface Props {
  cityReports: IncidentFeature[];
  iticEvents: IncidentFeature[];
  /**
   * The AQI reading the dashboard shows everywhere else — the station
   * reading from the air-quality trend feed.
   *
   * It used to be the Open-Meteo grid model, which put a second, different
   * AQI on the same screen as the world strip's (93 against 126, both
   * "US AQI") and presented a model as if it were a measurement.
   */
  aqi: AirBriefInput | null;
  weather: WeatherSnapshot[];
  ageMinutes?: number;
  /**
   * The tier of the feed each tile is built from.
   *
   * A feed that has not answered is not evidence that nothing is happening.
   * Without this, a dead traffic feed printed "0 — NOMINAL" in the summary,
   * which is the one place in the dashboard a reader looks first.
   */
  cityReportsTier?: FallbackTier | "loading";
  iticTier?: FallbackTier | "loading";
  airTier?: FallbackTier | "loading";
  weatherTier?: FallbackTier | "loading";
}

const NEUTRAL = "var(--text-3)";

/** The colour of a count, by its level. */
const LEVEL_COLOR: Record<KpiLevel, string> = {
  critical: "var(--bad)",
  elevated: "var(--warn)",
  open: "var(--text)",
  clear: "var(--good)",
  unknown: NEUTRAL,
};

export function KpiStrip({
  cityReports,
  iticEvents,
  aqi,
  weather,
  ageMinutes,
  cityReportsTier,
  iticTier,
  airTier,
  weatherTier,
}: Props) {
  const openReports = cityReports.filter((r) => r.status !== "resolved").length;
  const totalEvents = iticEvents.length;
  const w = weather[0];

  const reports = feedState(cityReportsTier);
  const traffic = feedState(iticTier);
  // Air eligibility is resolved from observation time by selectBriefAir.
  void airTier;
  const wx = feedState(weatherTier);

  const reportsLvl = reportsLevel(openReports, reports.known);
  const trafficLvl = trafficLevel(totalEvents, traffic.known);
  const reportsColor = LEVEL_COLOR[reportsLvl];
  const trafficColor = LEVEL_COLOR[trafficLvl];
  const aqValue = aqi?.aqi ?? null;
  const band = aqValue != null ? aqiBand(aqValue, aqi?.scale) : null;
  const aqColor = band?.level === "good" ? "var(--good)" : band?.level === "watch" ? "var(--warn)" : band?.level === "alert" ? "var(--bad)" : band?.level === "critical" ? "var(--crit)" : NEUTRAL;

  return (
    <>
      <PanelHeader title="CITY PULSE" ageMinutes={ageMinutes} source="traffy·itic·aqicn·openmeteo" />
      <div className="kpi-grid">
      <div className="kpi" role="status"
        style={{ "--kpi-accent": reportsColor } as React.CSSProperties}
        aria-label={reports.known ? `Citizen reports: ${openReports} open` : "Citizen reports unavailable"}>
        <div className="label">TRAFFY:CR</div>
        <div className="value" style={{ color: reportsColor }}>
          {reports.known ? openReports : "—"}
          <span className="kpi-status-word">{reports.word ?? (reportsLvl === "open" ? LEVEL_WORD.open : LEVEL_WORD[reportsLvl])}</span>
        </div>
        <div className="sub">{reports.known ? `${cityReports.length} TOTAL // OPEN` : "No observation"}</div>
      </div>

      <div className="kpi" role="status"
        style={{ "--kpi-accent": trafficColor } as React.CSSProperties}
        aria-label={traffic.known ? `Traffic events: ${totalEvents}` : "Traffic events unavailable"}>
        <div className="label">iTIC:EVT</div>
        <div className="value" style={{ color: trafficColor }}>
          {traffic.known ? totalEvents : "—"}
          <span className="kpi-status-word">{traffic.word ?? (trafficLvl === "open" ? "ACTIVE" : LEVEL_WORD[trafficLvl])}</span>
        </div>
        <div className="sub">{traffic.known ? "MUNICIPAL AREA" : "No observation"}</div>
      </div>

      <div className="kpi" role="status"
        style={{ "--kpi-accent": aqColor } as React.CSSProperties}
        aria-label={aqValue != null ? `AQI ${aqValue}, ${band?.en}` : "AQI unavailable"}>
        <div className="label">{aqi?.scale === "thai-pcd" ? "PCD AQI · STATION" : "US AQI · MODEL"}</div>
        <div className="value" style={{ color: aqColor }}>
          {aqValue ?? "—"}
          {aqValue != null && (
            <span className="kpi-status-word">{band?.en}</span>
          )}
        </div>
        <div className="sub" title={aqi?.station ?? "Forecast grid"}>{aqi ? `${aqi.pm25?.toFixed(1) ?? "—"} µg/m³${aqi.distanceKm != null ? ` · ${aqi.distanceKm.toFixed(1)} km from town` : " · grid model"}` : "No observation"}</div>
      </div>

      <div className="kpi" role="status"
        style={{ "--kpi-accent": wx.known ? "var(--data)" : NEUTRAL } as React.CSSProperties}
        aria-label={w?.tempC != null ? `Temperature ${Math.round(w.tempC)} degrees` : "Temperature unavailable"}>
        <div className="label">TEMP:WX</div>
        <div className="value" style={{ color: wx.known ? "var(--text)" : NEUTRAL }}>
          {wx.known && w?.tempC != null ? `${Math.round(w.tempC)}°` : "—"}
          {wx.known && w?.tempC != null && <span className="kpi-status-word">{w.feelsLikeC != null ? `FEELS ${Math.round(w.feelsLikeC)}°` : ""}</span>}
        </div>
        <div className="sub">{wx.known && w ? `${w.windKmh?.toFixed(0) ?? "—"} KMH` : "No observation"}</div>
      </div>
    </div>
    </>
  );
}
